import { useToast } from "@chakra-ui/react";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { VaultContext } from "@equidefi/portals/context/VaultProvider";
import { useGetAccreditationFiles, useUpdateVault } from "@equidefi/portals/hooks/useAccreditation";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import {
  hasProcessingPlaidFile,
  offeringIsIPO,
  offeringIsRegS,
  offeringIsTerminated,
  offeringIsTypeRegA,
} from "@equidefi/shared";

import { useLockHistory } from "@/hooks/useLockHistory";
import useWorkflowPosition from "@/hooks/useWorkflowPosition";
import { useWorkflowContext } from "@/pages/workflow/context/WorkflowContext";

import WorkflowStepError from "../WorkflowStepError";
import { AccreditationSelect } from "./AccreditationSelect";
import { AdditionalInfoForm } from "./AdditionalInfo";

import { AccreditationUploadSection } from "@equidefi/portals/components/accreditation/AccreditationUploadSection";
import { WorkflowContainer } from "../WorkflowContainer";

const REFETCH_INTERVAL = 30_000;

const offeringShowsAdditionalInfo = (offering) =>
  (offeringIsTypeRegA(offering) || offeringIsIPO(offering) || offeringIsRegS(offering)) &&
  offering.collect_additional_info;

export const WorkflowAccreditation = () => {
  const { investment, analytics, offering } = useWorkflowContext();
  const isOfferingTerminated = offeringIsTerminated(offering);
  const { vault, refetch } = useContext(VaultContext);
  const updateVault = useUpdateVault(investment?.id as string);
  const navigate = useNavigate();
  useLockHistory();

  useDocumentTitle(["Accreditation", offering?.name, investment?.issuer?.name]);

  const {
    data: accreditationFiles,
    refetch: refetchFiles,
    isFetching,
  } = useGetAccreditationFiles(vault?.id, {
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    // Poll while a Plaid report is still generating so its download CTA goes live
    // automatically once the async job finishes.
    refetchInterval: (files) => (hasProcessingPlaidFile(files) ? REFETCH_INTERVAL : false),
  });

  /**
   * Refetch both the vault and accreditation files, not just one or the other
   */
  const handleRefetch = async () => {
    refetch();
    if (vault?.id) await refetchFiles();
  };

  useWorkflowPosition(8);
  const toast = useToast();
  const [reupload, setReupload] = useState(false);

  useEffect(() => {
    handleRefetch();
  }, []);

  const handleReupload = () => {
    setReupload(true);
  };

  const navigateToVault = () => {
    navigate("/vault/dashboard", {
      state: {
        completed: true,
        isPaid: investment?.payments?.filter((p) => p.status === "succeeded")?.length > 0,
        investment_id: investment?.id,
      },
    });
  };

  const canCompleteInvestment = useMemo(
    () => !isFetching && accreditationFiles && accreditationFiles?.length > 0 && vault?.utility_bill,
    [accreditationFiles, vault?.utility_bill, isFetching],
  );

  const onSubmit = async () => {
    try {
      try {
        await updateVault.mutateAsync({ submitted: true });
        analytics.track("complete");
        navigateToVault();
      } catch (error) {
        console.log(error);
        toast({
          status: "error",
          description: "Failed to update your investment",
        });
      }
    } catch (error) {
      console.log(error);
      toast({
        status: "error",
        description: "Failed to submit your files",
      });
    }
  };

  if (isOfferingTerminated) {
    return (
      <WorkflowStepError
        title="Accreditation"
        text="This offering has been terminated. No document uploads can be made."
        buttonLabel="Offering Terminated"
        variant="error"
      />
    );
  }

  if (offeringShowsAdditionalInfo(offering) && offering.collect_additional_info) {
    return <AdditionalInfoForm />;
  }

  if (!reupload && vault?.status === "APPROVED") {
    return <AccreditationSelect navigateToVault={navigateToVault} onReupload={handleReupload} />;
  }

  return (
    <WorkflowContainer
      title="Accreditation Documents"
      onContinue={onSubmit}
      buttonLabel="Submit Investment"
      buttonProps={{
        isDisabled: !canCompleteInvestment,
      }}>
      <AccreditationUploadSection
        vault={vault}
        investment={investment}
        offering={offering}
        accreditationFiles={accreditationFiles}
        refetch={handleRefetch}
      />
    </WorkflowContainer>
  );
};
